import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Snackbar,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  getCurrencyByCountryId,
  getCurrencySymbol,
} from "../utils/getCurrencyByCountry";
import { userHasActiveEntitlement, userHasPaidEntitlement } from "../../utils/entitlements";
import { trackEvent } from "../../utils/analytics/gtag";

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const { country: lang } = useParams(); // e.g., "el", "en", etc.
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hydrateFromCurrentUser } = useAuth();

  // Mapping lang to backend country ID and country code
  const langToCountryId = {
    el: 1, // Ελλάδα
    gr: 1, // Greece (region-style route)
    en: 1, // English language route, Greece-first launch scope
    us: 2, // USA (region-style route)
    ro: 3, // Ρουμανία
    it: 4, // Ιταλία
    es: 5, // Ισπανία
  };

  const langToCountry = {
    el: "gr",
    gr: "gr",
    en: "gr",
    us: "us",
    ro: "ro",
    it: "it",
    es: "es",
  };

  const countryId = langToCountryId[lang] || 1;
  const countryCode = langToCountry[lang] || "gr";

  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("eur");
  const [currencyDefault, setCurrencyDefault] = useState("eur");
  const [pricingCatalog, setPricingCatalog] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelSuccess, setCancelSuccess] = useState("");
  const [cancelEffectiveDate, setCancelEffectiveDate] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [autoCheckoutError, setAutoCheckoutError] = useState("");
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState("");
  const entitlements = user?.entitlements || null;
  const hasActiveSub = userHasActiveEntitlement(user);
  const hasPaidSubByDate = userHasPaidEntitlement(user);
  const hasActiveSubscription = hasActiveSub;
  const isPremiumFlag = Boolean(entitlements?.is_premium);
  const isPaidPremiumFlag = hasPaidSubByDate;
  const tier = String(entitlements?.tier || (user ? "free" : "guest")).toLowerCase();
  const planType = String(user?.subscription_plan || user?.plan || "free").toLowerCase();
  const trialEndsAt = entitlements?.trial_active ? entitlements?.expires_at || null : null;
  const subscriptionExpiresAt = entitlements?.subscription_active ? entitlements?.expires_at || null : null;
  const hasTrialActive = Boolean(entitlements?.trial_active);
  const subscriptionPlan = String(user?.subscription_plan || "free").toLowerCase();
  const hasNonFreePlan = subscriptionPlan !== "free";
  const stripeSubscriptionStatus = String(user?.stripe_subscription_status || "").toLowerCase();
  const paymentIssueStatuses = new Set(["past_due", "unpaid", "incomplete", "incomplete_expired"]);
  const hasPaymentIssue = Boolean(
    user &&
    paymentIssueStatuses.has(stripeSubscriptionStatus) &&
    (hasNonFreePlan || isPremiumFlag || hasActiveSubscription)
  );
  const showManageSubscription = Boolean(user && (isPaidPremiumFlag || user?.stripe_customer_id));
  const showPaymentHistory = Boolean(user?.stripe_customer_id || isPaidPremiumFlag || hasNonFreePlan);
  const userTier = !user ? "guest" : isPaidPremiumFlag ? "premium_paid" : hasTrialActive ? "trial" : "free";

  // Set currency/symbol based on country
  useEffect(() => {
    const curr = getCurrencyByCountryId(countryId);
    setCurrency(curr);
  }, [countryId]);

  const isDev = process.env.NODE_ENV !== "production";
  const queryParams = React.useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const queryCurrencyOverride = React.useMemo(() => {
    if (!isDev) return null;
    const value = (queryParams.get("currency") || "").trim().toLowerCase();
    return value === "eur" || value === "usd" ? value : null;
  }, [isDev, queryParams]);

  const autoCheckoutIntent = (queryParams.get("intent") || "").trim().toLowerCase() === "premium";
  const autoCheckoutPlan = ((queryParams.get("plan") || "").trim().toLowerCase() === "yearly")
    ? "yearly"
    : "monthly";
  const funnelIntent = autoCheckoutIntent ? "premium" : null;

  // Load pricing catalog from canonical public endpoint.
  useEffect(() => {
    if (!countryId) return;
    setPricingCatalog([]);
    const fetchPrices = async () => {
      try {
        const res = await api.public.getPricingCatalog(countryId);
        const plans = Array.isArray(res?.data?.plans) ? res.data.plans : [];
        const fallbackCurrency = String(res?.data?.currency_default || "eur").toLowerCase();
        setCurrencyDefault(fallbackCurrency === "usd" ? "usd" : "eur");
        setPricingCatalog(plans);
      } catch (err) {
        setPricingCatalog([]);
      }
    };
    fetchPrices();
  }, [countryId]);

  useEffect(() => {
    if (!user) {
      setPaymentHistory([]);
      return;
    }
    const fetchHistory = async () => {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const res = await api.users.paymentHistory();
        setPaymentHistory(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setHistoryError(
          err?.response?.data?.error ||
            t("failed_to_load_payment_history") ||
            "Failed to load payment history."
        );
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [user, t]);

  const selectedCurrency = queryCurrencyOverride || currency;
  const effectiveCurrency = React.useMemo(() => {
    if (pricingCatalog.some((p) => String(p?.currency || "").toLowerCase() === selectedCurrency)) {
      return selectedCurrency;
    }
    if (pricingCatalog.some((p) => String(p?.currency || "").toLowerCase() === currencyDefault)) {
      return currencyDefault;
    }
    return selectedCurrency;
  }, [pricingCatalog, selectedCurrency, currencyDefault]);

  const findPlan = (plan) => (
    pricingCatalog.find(
      (p) => p?.plan === plan && String(p?.currency || "").toLowerCase() === effectiveCurrency
    ) || null
  );
  const parseAmount = (amountDecimal) => {
    const n = Number(amountDecimal);
    return Number.isFinite(n) ? n : null;
  };
  const formatPlanAmountInterval = (planObj) => {
    if (!planObj) return "--";
    return `${planObj.amount_decimal} ${String(planObj.currency || "").toUpperCase()} / ${planObj.interval}`;
  };
  const MONTHLY_PLAN = findPlan("monthly");
  const YEARLY_PLAN = findPlan("yearly");
  const selectedAutoPlan = autoCheckoutPlan === "yearly" ? YEARLY_PLAN : MONTHLY_PLAN;
  const MONTHLY_PRICE = parseAmount(MONTHLY_PLAN?.amount_decimal);
  const YEARLY_PRICE = parseAmount(YEARLY_PLAN?.amount_decimal);
  const YEARLY_MONTHLY_EQUIVALENT = YEARLY_PRICE ? (YEARLY_PRICE / 12).toFixed(2) : "--";
  const YEARLY_MONTHLY_EQUIVALENT_LABEL = YEARLY_PLAN
    ? `${YEARLY_MONTHLY_EQUIVALENT} ${String(YEARLY_PLAN.currency || "").toUpperCase()} / month`
    : "--";

  useEffect(() => {
    trackEvent("funnel_subscription_view", {
      lang: lang || "en",
      country: countryCode,
      plan: autoCheckoutPlan,
      currency: effectiveCurrency,
      intent: funnelIntent,
      user_tier: userTier,
    });
  }, [lang, countryCode, autoCheckoutPlan, effectiveCurrency, funnelIntent, userTier]);

  const getAutoCheckoutGuardKey = useCallback((planNorm, currencyCode) => {
    const userId = String(user?.id || "").trim();
    if (!userId) return null;
    return `autocheckout:${userId}:${planNorm}:${currencyCode}`;
  }, [user?.id]);

  const clearAutoCheckoutGuardsForCurrentUser = useCallback(() => {
    const userId = String(user?.id || "").trim();
    if (!userId || typeof window === "undefined") return;
    const prefix = `autocheckout:${userId}:`;
    for (let i = window.sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(prefix)) {
        window.sessionStorage.removeItem(key);
      }
    }
  }, [user?.id]);

  const removePremiumIntentFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search || "");
    params.delete("intent");
    params.delete("plan");
    const next = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: next ? `?${next}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const startCheckout = useCallback(async (plan, { allowLoginRedirect = true } = {}) => {
    const planNorm = plan === "yearly" ? "yearly" : "monthly";
    const premiumReturnTo = `/${lang}/subscription?intent=premium&plan=${planNorm}`;
    const premiumLoginQuery = new URLSearchParams({
      intent: "premium",
      plan: planNorm,
      returnTo: premiumReturnTo,
    }).toString();
    if (!user) {
      if (allowLoginRedirect) {
        navigate(`/${lang}/login?${premiumLoginQuery}`);
      }
      return;
    }

    setLoading(true);
    try {
      const res = await api.stripe.createCheckoutSession({
        plan: planNorm,
        currency: effectiveCurrency,
        country: countryCode,
      });
      trackEvent("funnel_checkout_session_created", {
        lang: lang || "en",
        country: countryCode,
        plan: planNorm,
        currency: effectiveCurrency,
        intent: funnelIntent,
        user_tier: userTier,
      });
      trackEvent("funnel_checkout_redirect", {
        lang: lang || "en",
        country: countryCode,
        plan: planNorm,
        currency: effectiveCurrency,
        intent: funnelIntent,
        user_tier: userTier,
      });
      window.location.href = res.data.checkout_url;
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      if ((status === 401 || status === 403) && allowLoginRedirect) {
        setLoading(false);
        navigate(`/${lang}/login?${premiumLoginQuery}`);
        return { ok: false, redirectedToLogin: true };
      }
      setLoading(false);
      return {
        ok: false,
        errorMessage:
          err?.response?.data?.error ||
          t("subscription_checkout_error") ||
          "Failed to start Stripe checkout.",
      };
    }
  }, [countryCode, effectiveCurrency, funnelIntent, lang, navigate, t, user, userTier]);

  const handleStripePayment = async (plan) => {
    setAutoCheckoutError("");
    const planNorm = plan === "yearly" ? "yearly" : "monthly";
    trackEvent("subscribe_click", {
      plan: "premium",
      billing_period: planNorm === "yearly" ? "year" : "month",
      currency: effectiveCurrency ? effectiveCurrency.toUpperCase() : null,
      region: String(countryCode || "").toUpperCase(),
      is_guest: !user,
    });
    trackEvent("funnel_checkout_click", {
      lang: lang || "en",
      country: countryCode,
      plan: planNorm,
      currency: effectiveCurrency,
      intent: "premium",
      user_tier: userTier,
    });
    const result = await startCheckout(plan, { allowLoginRedirect: true });
    if (result && result.ok === false && !result.redirectedToLogin) {
      alert(result.errorMessage || (t("subscription_checkout_error") || "Failed to start Stripe checkout."));
    }
  };

  useEffect(() => {
    if (hasActiveSub) {
      clearAutoCheckoutGuardsForCurrentUser();
    }
  }, [hasActiveSub, clearAutoCheckoutGuardsForCurrentUser]);

  // Auto-checkout path for explicit premium intent.
  useEffect(() => {
    if (!autoCheckoutIntent) return;
    if (!user?.id) return;

    if (hasActiveSub) {
      clearAutoCheckoutGuardsForCurrentUser();
      removePremiumIntentFromUrl();
      return;
    }

    if (!selectedAutoPlan) return; // wait until pricing catalog loads for selected plan/currency

    const guardKey = getAutoCheckoutGuardKey(autoCheckoutPlan, effectiveCurrency);
    if (!guardKey || typeof window === "undefined") return;
    if (window.sessionStorage.getItem(guardKey) === "1") return;

    window.sessionStorage.setItem(guardKey, "1");
    trackEvent("funnel_checkout_autostart", {
      lang: lang || "en",
      country: countryCode,
      plan: autoCheckoutPlan,
      currency: effectiveCurrency,
      intent: "premium",
      user_tier: userTier,
    });
    let cancelled = false;

    (async () => {
      const result = await startCheckout(autoCheckoutPlan, { allowLoginRedirect: false });
      if (cancelled) return;
      if (!result?.ok) {
        setAutoCheckoutError(
          result?.errorMessage ||
            t("subscription_checkout_error") ||
            "We could not start checkout automatically. Please try again."
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    autoCheckoutIntent,
    autoCheckoutPlan,
    effectiveCurrency,
    hasActiveSub,
    selectedAutoPlan,
    user?.id,
    t,
    clearAutoCheckoutGuardsForCurrentUser,
    removePremiumIntentFromUrl,
    getAutoCheckoutGuardKey,
    startCheckout,
    countryCode,
    lang,
    userTier,
  ]);

  const formatDate = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleDateString(lang || "en");
  };

  const formatAmount = (amount, curr) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return "-";
    const c = (curr || currency || "EUR").toUpperCase();
    try {
      return new Intl.NumberFormat(lang || "en", {
        style: "currency",
        currency: c,
      }).format(Number(amount));
    } catch {
      return `${Number(amount).toFixed(2)} ${c}`;
    }
  };

  const locale = lang === "el" || lang === "gr" ? "el-GR" : "en-US";
  const parseIsoDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const formatStatusDate = (value) => {
    const d = parseIsoDate(value);
    if (!d) return "-";
    return d.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const nowDate = new Date();
  const subscriptionDateObj = parseIsoDate(subscriptionExpiresAt);
  const trialDateObj = parseIsoDate(trialEndsAt);
  const trialDaysRemaining = trialDateObj && trialDateObj > nowDate
    ? Math.max(1, Math.ceil((trialDateObj.getTime() - nowDate.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const latestPastDate = [subscriptionDateObj, trialDateObj]
    .filter((d) => d && d < nowDate)
    .sort((a, b) => b - a)[0] || null;
  const isGreek = locale === "el-GR";
  const statusDateLabel = (() => {
    if (isPaidPremiumFlag && subscriptionDateObj) {
      if (subscriptionDateObj >= nowDate) {
        return isGreek ? "Ανανέωση στις" : "Renews on";
      }
      return isGreek ? "Έληξε στις" : "Expired on";
    }
    if (hasTrialActive && trialDateObj) {
      return isGreek ? "Η δοκιμή λήγει στις" : "Trial ends on";
    }
    if (latestPastDate) {
      return isGreek ? "Έληξε στις" : "Expired on";
    }
    return null;
  })();
  const statusDateValue = (() => {
    if (isPaidPremiumFlag && subscriptionDateObj) return formatStatusDate(subscriptionExpiresAt);
    if (hasTrialActive && trialDateObj) return formatStatusDate(trialEndsAt);
    if (latestPastDate) return formatStatusDate(latestPastDate.toISOString());
    return null;
  })();

  const statusLabel = (() => {
    if (!user) return t("guest") || "Guest";
    if (isPaidPremiumFlag) return isGreek ? "Premium συνδρομή" : "Premium subscription";
    if (hasTrialActive) {
      return isGreek
        ? `Δοκιμαστική περίοδος (${trialDaysRemaining} ημέρες απομένουν)`
        : `Trial (${trialDaysRemaining} days left)`;
    }
    return isGreek ? "Δωρεάν" : "Free";
  })();

  const statusColor = (() => {
    if (!user) return "default";
    if (isPaidPremiumFlag) return "success";
    if (hasTrialActive) return "warning";
    return "primary";
  })();

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError("");
    setCancelSuccess("");
    setCancelEffectiveDate("");
    try {
      const res = await api.stripe.cancelSubscription({ cancel_at_period_end: true });
      const periodEndRaw = res?.data?.current_period_end;
      let periodEndText = "";
      if (periodEndRaw) {
        const dt = new Date(Number(periodEndRaw) * 1000);
        if (!Number.isNaN(dt.getTime())) {
          periodEndText = dt.toLocaleDateString(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }
      }
      const msg = periodEndText
        ? (isGreek
          ? `Η ακύρωση προγραμματίστηκε. Ακυρώνεται στις ${periodEndText}.`
          : `Cancellation scheduled. Cancels on ${periodEndText}.`)
        : (isGreek ? "Η ακύρωση προγραμματίστηκε." : "Cancellation scheduled.");
      setCancelEffectiveDate(periodEndText);
      setCancelSuccess(msg);
      setCancelDialogOpen(false);
      setSnackbarOpen(true);
      await hydrateFromCurrentUser();
    } catch (err) {
      setCancelError(
        err?.response?.data?.error ||
          t("failed_to_cancel_subscription") ||
          "Failed to cancel subscription."
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!user) return;
    setBillingPortalError("");
    setBillingPortalLoading(true);
    try {
      const res = await api.stripe.createBillingPortalSession({ country: lang || "el" });
      const portalUrl = res?.data?.portal_url;
      if (!portalUrl) {
        throw new Error("Missing portal URL");
      }
      window.location.href = portalUrl;
    } catch (err) {
      setBillingPortalError(
        err?.response?.data?.error ||
          (isGreek
            ? "Δεν ήταν δυνατή η μετάβαση στη διαχείριση χρέωσης."
            : "Failed to open billing portal.")
      );
    } finally {
      setBillingPortalLoading(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: 5,
        bgcolor: (theme) => theme.palette.background.paper,
        color: (theme) => theme.palette.text.primary,
        borderRadius: 4,
        boxShadow: 4,
        p: { xs: 2, md: 4 },
      }}
    >
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontWeight: 700,
          mb: 2,
          letterSpacing: 1,
        }}
      >
        {t("my_subscription")}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Box>
        {autoCheckoutIntent && !hasActiveSub ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {(t("premium_one_step_banner") || "You're 1 step away from Premium")}{" "}
            <strong>({autoCheckoutPlan === "yearly" ? "Yearly" : "Monthly"})</strong>
          </Alert>
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {t("premium_features") || "Premium features"}
        </Typography>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#1c2530" : "#e3f2fd",
            color: (theme) => theme.palette.text.primary,
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {MONTHLY_PRICE && YEARLY_PRICE
            ? t("subscription_explainer", {
                monthly: formatPlanAmountInterval(MONTHLY_PLAN),
                yearly: formatPlanAmountInterval(YEARLY_PLAN),
                yearly_month: YEARLY_MONTHLY_EQUIVALENT_LABEL,
                currency: effectiveCurrency.toUpperCase(),
              })
            : t("loading_prices")}
        </Alert>
        {autoCheckoutError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {autoCheckoutError}
          </Alert>
        ) : null}
        {hasPaymentIssue ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={(
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleOpenBillingPortal}
                disabled={billingPortalLoading}
              >
                {billingPortalLoading
                  ? (isGreek ? "Φόρτωση..." : "Loading...")
                  : (isGreek ? "Διαχείριση χρέωσης" : "Manage billing")}
              </Button>
            )}
          >
            {isGreek
              ? "Η πληρωμή απέτυχε. Ενημέρωσε τη μέθοδο πληρωμής σου για να διατηρήσεις την Premium πρόσβαση."
              : "Payment failed. Update your payment method to keep premium access."}
          </Alert>
        ) : null}
        {billingPortalError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {billingPortalError}
          </Alert>
        ) : null}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="body2">• {t("premium_value_basket") || "Cheapest-basket savings and smarter shopping decisions"}</Typography>
          <Typography variant="body2">• {t("premium_value_history") || "365-day price history, alerts, and price watch context"}</Typography>
          <Typography variant="body2">• {t("premium_value_planning") || "Unlimited meal planning tied to real shopping costs"}</Typography>
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !MONTHLY_PRICE}
            onClick={() => handleStripePayment("monthly")}
            sx={{ minWidth: 140 }}
          >
            {t("choose_monthly", {
              price: MONTHLY_PLAN
                ? `${MONTHLY_PLAN.amount_decimal} ${String(MONTHLY_PLAN.currency || "").toUpperCase()}`
                : "--",
              currency: getCurrencySymbol(effectiveCurrency),
            })}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={loading || !YEARLY_PRICE}
            onClick={() => handleStripePayment("yearly")}
            sx={{ minWidth: 140 }}
          >
            {t("choose_yearly", {
              price: YEARLY_PLAN
                ? `${YEARLY_PLAN.amount_decimal} ${String(YEARLY_PLAN.currency || "").toUpperCase()}`
                : "--",
              price_month: YEARLY_PLAN
                ? `${YEARLY_MONTHLY_EQUIVALENT} ${String(YEARLY_PLAN.currency || "").toUpperCase()}`
                : "--",
              currency: getCurrencySymbol(effectiveCurrency),
            })}
          </Button>
        </Box>
        {loading && (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          {t("cancel_anytime")}
        </Typography>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          {t("subscription_features_soon")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("subscription_support_info")}{" "}
          <a href="mailto:support@incooking.eu">support@incooking.eu</a>.
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {t("current_plan") || "Current plan"}
        </Typography>
        <Card
          id="subscription-status-card"
          data-testid="subscription-status-card"
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <CardContent>
            <Stack spacing={1.25}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body1">
                  <b>{t("status") || "Status"}:</b> {statusLabel}
                </Typography>
                <Chip label={statusLabel} color={statusColor} size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                <b>{t("tier") || "Tier"}:</b>{" "}
                {tier === "premium_paid"
                  ? (isGreek ? "premium_paid (Premium συνδρομή)" : "premium_paid (Premium subscription)")
                  : tier === "trial"
                    ? (isGreek ? "trial (Δοκιμαστική περίοδος)" : "trial (Trial)")
                    : tier === "free"
                      ? (isGreek ? "free (Δωρεάν)" : "free (Free)")
                      : tier}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{t("plan") || "Plan"}:</b> {planType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{t("has_access") || "has_access"}:</b> {hasActiveSubscription ? "true" : "false"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{t("is_premium") || "is_premium"}:</b> {isPremiumFlag ? "true" : "false"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{t("subscription_active") || "subscription_active"}:</b> {isPaidPremiumFlag ? "true" : "false"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>{t("stripe_subscription_status") || "stripe_subscription_status"}:</b>{" "}
                {stripeSubscriptionStatus || "-"}
              </Typography>
              {statusDateLabel && statusDateValue ? (
                <Typography variant="body2" color="text.secondary">
                  <b>{statusDateLabel}:</b> {statusDateValue}
                </Typography>
              ) : null}
              {cancelEffectiveDate ? (
                <Typography variant="body2" color="warning.main">
                  <b>{isGreek ? "Ακυρώνεται στις" : "Cancels on"}:</b> {cancelEffectiveDate}
                </Typography>
              ) : null}

              {!user ? (
                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                  <Button variant="contained" onClick={() => navigate(`/${lang}/login`)}>
                    {t("login_btn") || "Login"}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate(`/${lang}/register`)}>
                    {t("register_btn") || "Register"}
                  </Button>
                </Stack>
              ) : null}

              {user && !isPaidPremiumFlag ? (
                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={() => handleStripePayment("monthly")}
                    disabled={loading || !MONTHLY_PRICE}
                  >
                    {t("upgrade_now") || "Upgrade now"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleStripePayment("yearly")}
                    disabled={loading || !YEARLY_PRICE}
                  >
                    {t("choose_yearly", {
                      price: YEARLY_PLAN
                        ? `${YEARLY_PLAN.amount_decimal} ${String(YEARLY_PLAN.currency || "").toUpperCase()}`
                        : "--",
                      price_month: YEARLY_PLAN
                        ? `${YEARLY_MONTHLY_EQUIVALENT} ${String(YEARLY_PLAN.currency || "").toUpperCase()}`
                        : "--",
                      currency: getCurrencySymbol(effectiveCurrency),
                    })}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {showManageSubscription ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t("manage_subscription") || "Manage subscription"}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.2}>
              <Box>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={cancelLoading || !user}
                >
                  {t("cancel_subscription_now") || "Cancel subscription"}
                </Button>
              </Box>
              {cancelError ? <Alert severity="error">{cancelError}</Alert> : null}
              {cancelSuccess ? <Alert severity="success">{cancelSuccess}</Alert> : null}
            </Stack>
          </Paper>
        </Box>
      ) : null}

      {showPaymentHistory ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t("payment_history") || "Payment history"}
          </Typography>
          {historyLoading ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : historyError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {historyError}
            </Alert>
          ) : paymentHistory.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("date") || "Date"}</TableCell>
                    <TableCell>{t("amount") || "Amount"}</TableCell>
                    <TableCell>{t("status") || "Status"}</TableCell>
                    <TableCell>{t("invoice") || "Invoice"}</TableCell>
                    <TableCell>{t("pdf") || "PDF"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.map((row, index) => (
                    <TableRow
                      key={
                        row.invoice_id ||
                        `${row.date}-${row.amount_paid}-${row.currency || ""}-${row.status || ""}-${index}`
                      }
                    >
                      <TableCell>{formatDate(row.date)}</TableCell>
                      <TableCell>{formatAmount(row.amount_paid, row.currency)}</TableCell>
                      <TableCell>{row.status || "-"}</TableCell>
                      <TableCell>
                        {row.hosted_invoice_url ? (
                          <a href={row.hosted_invoice_url} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {row.pdf_link ? (
                          <a href={row.pdf_link} target="_blank" rel="noreferrer">
                            PDF
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("no_invoices_yet") || "No invoices yet"}
              </Typography>
            </Paper>
          )}
        </Box>
      ) : null}

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t("confirm_cancel") || "Cancel at period end"}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("confirm_cancel_subscription_text") ||
              (isGreek
                ? "Η συνδρομή σου θα παραμείνει ενεργή μέχρι το τέλος της τρέχουσας περιόδου. Θέλεις να προχωρήσεις;"
                : "Your subscription will remain active until the end of the current billing period. Do you want to continue?")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading
              ? t("processing") || "Processing..."
              : t("confirm") || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSnackbarOpen(false)}
        message={cancelSuccess}
      />
    </Container>
  );
}
